import { useState, useEffect } from 'react';

interface GitHubStats {
    recentCommits: {
        message: string;
        repo: string;
        date: string;
        url: string;
    }[];
    totalCommits: number;
    loading: boolean;
    error: string | null;
}

export function useGitHub(username: string | undefined) {
    const [stats, setStats] = useState<GitHubStats>({
        recentCommits: [],
        totalCommits: 0,
        loading: false,
        error: null
    });

    useEffect(() => {
        if (!username) return;

        const fetchGitHubData = async () => {
            setStats(prev => ({ ...prev, loading: true, error: null }));
            try {
                // Fetch public user info & events in parallel
                const userPromise = fetch(`https://api.github.com/users/${username}`);
                const eventsPromise = fetch(`https://api.github.com/users/${username}/events/public`);

                // Fetch commits via search (requires a term, so we use the username)
                const searchPromise = fetch(`https://api.github.com/search/commits?q=author:${username}+${username}`, {
                    headers: { 'Accept': 'application/vnd.github.cloak-preview' }
                });

                const [userRes, eventsRes, searchRes] = await Promise.all([
                    userPromise,
                    eventsPromise,
                    searchPromise
                ]);

                let totalCommits = 0;
                let recentCommits = [];

                // Handle search data (most accurate for commit count if term matches)
                if (searchRes.ok) {
                    const searchData = await searchRes.json();
                    totalCommits = searchData.total_count || 0;
                }

                // Handle events (for the ticker)
                if (eventsRes.ok) {
                    const events = await eventsRes.json();
                    const pushEvents = Array.isArray(events) ? events.filter((event: any) => event.type === 'PushEvent') : [];
                    recentCommits = pushEvents.flatMap((event: any) =>
                        event.payload.commits.map((commit: any) => ({
                            message: commit.message,
                            repo: event.repo.name.split('/')[1],
                            date: event.created_at,
                            url: `https://github.com/${event.repo.name}/commit/${commit.sha}`
                        }))
                    ).slice(0, 5);
                }

                // If totalCommits is still 0, we can use public_repos as a very rough signal
                if (totalCommits === 0 && userRes.ok) {
                    const userData = await userRes.json();
                    // We don't set totalCommits to repos, but we can log it
                    console.log(`Public repos: ${userData.public_repos}`);
                }

                setStats({
                    recentCommits,
                    totalCommits,
                    loading: false,
                    error: null
                });
            } catch (err: any) {
                console.error('Error in useGitHub hook:', err);
                setStats(prev => ({ ...prev, loading: false, error: err.message }));
            }
        };

        fetchGitHubData();
    }, [username]);

    return stats;
}
